import userData from '../user-data';
import type { UserData, TireSize } from '../user-data';
import fitmentApi from './fitment-api';
import type { LicenseData } from './fitment-api';
import { t } from '../i18n';
import { isInViewport } from '../utils';
import Mustache from 'mustache';

import type { TireSelector } from './tireselector';

type State = {
  license: string | null;
  country: string | null;
};

class License {
  dom: {
    root: HTMLElement, // This module's root element
    form: HTMLFormElement,
    formSubmitBtn: HTMLButtonElement,
    license: HTMLInputElement,
    country: HTMLInputElement,
    errorMessage: HTMLElement,
    sizesContainer: HTMLElement,
  };

  parent: TireSelector;
  name = 'License';

  // Tyre selector filters state
  #state: State = {
    license: null,
    country: null,
  };

  #loading = false;

  availableSizes: TireSize[] = [];

  constructor(element: HTMLElement, parent: TireSelector) {
    this.dom = {
      root: element,
      form: element.querySelector('form[name="licenseSearchForm"]') as HTMLFormElement,
      formSubmitBtn: element.querySelector('button[type="submit"]') as HTMLButtonElement,
      license: element.querySelector('input[name="license"]') as HTMLInputElement,
      country: element.querySelector('input[name="country"]') as HTMLInputElement,
      errorMessage: element.querySelector('[data-error-message]') as HTMLElement,
      sizesContainer: element.querySelector('[data-sizes-container]') as HTMLElement,
    };

    this.parent = parent;
    this.name = `${this.parent.name} ${this.name}`;

    if(Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary dom elements found
      console.log(`[${this.name}] init()`);
      this.log('init settings', this);
      this.#initState();
      this.#attachEventListeners();
    } else {
      console.error(`[${this.name}]: missing data or dom elements`, this);
    }
  }

  #initState() {
    // Default state is empty
    const initialState = this.getState();

    // Check values from select elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if(this.dom[key]) {
        const value = this.dom[key].value;
        if(value) {
          initialState[key] = value;
        }
      }
    });

    // Check storage for user's selected size
    const userLicense = userData.get('license') as UserData['license'];
    if(userLicense) {
      initialState.license = userLicense.license;
      initialState.country = userLicense.country;
    }

    this.setState(initialState);
  }

  #attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));
    this.dom.license.addEventListener('keyup', this.onInputChange.bind(this));
    this.dom.license.addEventListener('blur', this.onInputChange.bind(this));

    // Delegated events for elements created dynamically
    this.dom.sizesContainer.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement;

      if(target.matches('select[name="size"]')) {
        // Size select event inside sizes container
        const sizeStr = target.value || '';

        if(sizeStr) {
          this.parent.onSizeSelect(sizeStr);
        }
      }
    });

    // Subscribe to user data changes
    userData.subscribe('license', this.onUserDataChange.bind(this));
  }

  /**
   * Returns current state of size filters
   */
  getState() {
    return {...this.#state};
  }

  setState(newState: State) {
    const oldState = this.getState();
    const futureState = { ...oldState, ...newState };

    this.#state = futureState;

    // Country should not change, as it is a hidden input.
    this.dom.license.value = futureState.license || '';

    if(futureState.license) {
      this.dom.formSubmitBtn.disabled = false;
    }
  }

  showErrorMessage(message: string) {
    if(!message) {
      return;
    }

    const textContainer = this.dom.errorMessage.querySelector('[data-error-message-text]');

    if(textContainer) {
      textContainer.textContent = message;
    } else {
      this.dom.errorMessage.textContent = message;
    }

    this.dom.errorMessage.removeAttribute('hidden');

    this.dom.license.setAttribute('aria-invalid', 'true');
    this.dom.license.setAttribute('aria-errormessage', this.dom.errorMessage.id);
  }

  hideErrorMessage() {
    const textContainer = this.dom.errorMessage.querySelector('[data-error-message-text]');

    if(textContainer) {
      textContainer.textContent = '';
    } else {
      this.dom.errorMessage.textContent = '';
    }

    this.dom.errorMessage.setAttribute('hidden', 'hidden');

    this.dom.license.removeAttribute('aria-invalid');
    this.dom.license.removeAttribute('aria-errormessage');
  }

  showSizeOptions(data: {license: string; vehicleData: LicenseData['vehicleData']} ) {
    this.log('showSizeOptions()', data);

    const carModel = [
      data.license,
      data.vehicleData.modelData.fullName,
      data.vehicleData.registrationData.yearOfFirstRegistration
    ].filter(Boolean).join(', ');

    const templateData = {
      carModel,
      sizes: this.availableSizes,
      sizesPlaceholder: t(`${this.parent.settings.lang}.tireselector__result__choose_size__placeholder`, {amount: this.availableSizes.length}),
    };

    this.dom.sizesContainer.innerHTML = Mustache.render(this.parent.sizesTemplate, templateData);
    this.dom.sizesContainer.hidden = false;

    if(!isInViewport(this.dom.sizesContainer)) {
      this.dom.sizesContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  clearSizeOptions() {
    this.dom.sizesContainer.innerHTML = '';
    this.dom.sizesContainer.hidden = true;
  }

  processVehicleData(data: LicenseData) {
    this.log('processVehicleData()', data);

    // Get vehicle manufacturer tire sizes
    const vehicleTireSizes = data?.vehicleData?.tyreFitmentData || [];

    // Match with available NT tires
    const availableSizes: TireSize[] = [];
    vehicleTireSizes.forEach(vSize => {
      // Check availability by comparing to sizeData
      const matches = this.parent.settings.sizeData.filter(item => {
        return vSize.tyreSize === item.size;
      });

      if(matches.length) {
        availableSizes.push({
          width: matches[0].width,
          height: matches[0].height,
          rim: matches[0].rim,
          size: vSize.tyreSize,
          default: vSize.isFactoryDefaultSize || false,
        });
      }
    });

    this.availableSizes = availableSizes;

    this.log('available sizes for vehicle', {vehicleTireSizes, availableSizes});

    // Store license data and available sizes to user data, clear others
    userData.store({
      license: data,
      availableSizes: availableSizes,
      vehicle: null,
      vehicleNA: null,
      selectedSize: null,
    });


    if(data?.vehicleData?.modelData) {
      this.parent.sendAnalyticsEvent({
        event: 'tyre_search_submit',
        tyre_search_type: 'License Search',
        vehicle_manufacturer: data.vehicleData.modelData.manufacturerName,
        vehicle_year: (data.vehicleData?.registrationData.yearOfFirstRegistration || 0).toString(),
        vehicle_model: data.vehicleData.modelData.modelName,
      });
    }

    if(this.parent.settings.autoRedirect && this.availableSizes.length > 0) {
      // Redirect to factory default size or if only one size available
      // showSizeOptions() handles showing message if no sizes found
      const selectedSize = this.availableSizes.find(size => size.default === true) || this.availableSizes[0];
      this.parent.onSizeSelect(selectedSize.size);
    } else {
      this.showSizeOptions({
        license: data.license,
        vehicleData: data.vehicleData,
      });
    }

  }

  onInputChange(event:Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if(!value || value.length <= 2) {
      this.dom.formSubmitBtn.disabled = true;
    } else {
      this.dom.formSubmitBtn.disabled = false;
    }

    this.hideErrorMessage();
  }

  onFormSubmit(event:Event) {
    event.preventDefault();

    if(this.#loading) {
      return;
    }

    this.log('onFormSubmit()');

    this.#loading = true;
    this.parent.setLoading(true);

    const license = this.dom.license.value.toUpperCase();
    const country = this.dom.country.value;

    fitmentApi.getVehicleDataFromLicense(license, country)
      .then((data) => {
        this.log('got vehicle data from license', data);
        this.processVehicleData(data);
      })
      .catch((error) => {
        console.error(`[${this.name}]: failed to get vehicle data from license`, error.message);
        const message = error.message || 'Failed to retrieve vehicle data. Please check your license and try again.';
        this.showErrorMessage(message);
      })
      .finally(() => {
        this.#loading = false;
        this.parent.setLoading(false);
      });
  }

  /**
   * Form reset clears user's data.
   * Customer request that all data should be cleared, not just license data.
   * Clearing saved license causes an UI update of this form
   */
  onFormReset(event:Event) {
    userData.store({license: null, vehicle: null, vehicleNA: null, availableSizes: null, selectedSize: null, selectedProduct: null});
  }

  onUserDataChange(licenseData: UserData['license']) {
    this.log('onUserDataChange()', licenseData);
    const currentState = this.getState();

    if(licenseData === null) {
      // Clear state
      this.setState({
        license: null,
        country: currentState.country,
      });

      this.clearSizeOptions();
    } else {
      // Don't set state if nothing has changed
      if(Object.keys(this.#state).every(key => this.#state[key] === licenseData[key])) {
        return;
      }

      this.setState({
        license: licenseData?.license || null,
        country: licenseData?.country || currentState.country,
      });

      this.clearSizeOptions();
    }
  }

  log(message: string, ...optionalParams: any[]) {
    // console.log(`[${this.name}] ${message}`, ...optionalParams);
  }
}

export default License;
